import React, { useEffect, type FC, type ReactNode } from 'react';
import { Box } from '@mui/material';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';

import { useMytaverseEvent } from '@core/dashboard/providers';

import { EditorAiDataProvider } from './AiScripts/EditorAiDataContext';
import { DriftProvider } from './DriftContext';
import { EditorModeProvider, useEditorMode } from './EditorModeContext';
import { EditorNodeKind } from './editorCategories';
import SceneTree from './SceneTree/SceneTree';
import Inspector from './Inspector/Inspector';
import BrandingBar from './BrandingBar/BrandingBar';
import { useEditorStyles } from './styles';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const UESelectionBridge = () => {
  const { selectedCustomizationUeId, customizationSelectionRevision } =
    useMytaverseEvent();
  const {
    selectSpot,
    selectRegionScript,
    setActiveFocus,
    activeFocus,
    setAiTreeSection,
    registerSceneClick,
  } = useEditorMode();

  useEffect(() => {
    if (!selectedCustomizationUeId) {
      return;
    }
    // Always remember the click — the AI Scripts create popover surfaces
    // these as "Recent scene clicks" even after the user switches focus.
    registerSceneClick(selectedCustomizationUeId);
    if (activeFocus === 'assistant') {
      // In AI focus, a spot click opens the AI inspector for that spot's
      // trigger. Spots live in the unified Scripts section, so make sure it's
      // active if the user was browsing Knowledge.
      setAiTreeSection('scripts');
      selectRegionScript(selectedCustomizationUeId, null);
      return;
    }
    if (activeFocus !== 'spots') {
      setActiveFocus('spots');
    }
    selectSpot(selectedCustomizationUeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCustomizationUeId, customizationSelectionRevision]);

  return null;
};

// When "follow live trigger" is enabled, walking into an AI trigger
// (SYNC_DATA_UE.audioTrigger.aiTriggerId) auto-opens the inspector for that
// trigger and switches the left panel to AI-trigger mode. When disabled, the
// virtual assistant (Noa) plays normally and the tree only highlights the row.
const AiTriggerSelectionBridge = () => {
  const { aiTriggerName } = useMytaverseEvent();
  const {
    activeFocus,
    followLiveTrigger,
    selectRegionScript,
    setAiTreeSection,
    selectedNode,
  } = useEditorMode();

  useEffect(() => {
    if (!followLiveTrigger || activeFocus !== 'assistant' || !aiTriggerName) {
      return;
    }
    // Skip if the inspector is already showing this trigger — avoids resetting
    // the panel as repeated SYNC_DATA_UE bursts arrive for the same trigger.
    if (
      selectedNode?.kind === EditorNodeKind.REGION_SCRIPT &&
      selectedNode.trigger === aiTriggerName
    ) {
      return;
    }
    setAiTreeSection('scripts');
    selectRegionScript(aiTriggerName, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiTriggerName, followLiveTrigger, activeFocus]);

  return null;
};

const EditorModeContent = () => {
  const styles = useEditorStyles();
  const { setBrandingActionRequest } = useMytaverseEvent();

  return (
    <Box sx={styles.root}>
      <UESelectionBridge />
      <AiTriggerSelectionBridge />
      <SceneTree />
      <Inspector />
      <Box sx={styles.brandingBarWrap}>
        <BrandingBar
          onCreateNew={() => setBrandingActionRequest('create')}
          onOpenLibrary={() => setBrandingActionRequest('library')}
        />
      </Box>
    </Box>
  );
};

const EditorMode = () => {
  const { isBrandingModeEnabled, brandingEditorFocus } = useMytaverseEvent();

  return (
    <AnimatePresence>
      {isBrandingModeEnabled ? (
        <motion.div
          key="editor-mode"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          style={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 1200,
          }}
        >
          <EditorModeProvider initialFocus={brandingEditorFocus}>
            <EditorAiDataProvider>
              <DriftProvider>
                <EditorModeContent />
              </DriftProvider>
            </EditorAiDataProvider>
          </EditorModeProvider>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default EditorMode;
